import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Director } from '../types';
import { filmmakersContent, ProjectData } from '../data/filmmakersContent';

interface FilmmakerPortfolioProps {
  director: Director | null;
  onBack: () => void;
  activeProjectId?: number | null;
  onSelectProject?: (id: number | null) => void;
  language?: string;
}

export const FilmmakerPortfolio: React.FC<FilmmakerPortfolioProps> = ({ 
  director, 
  onBack,
  activeProjectId,
  onSelectProject,
  language = 'ES'
}) => {
  const [videoEnded, setVideoEnded] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  
  const playerRef = useRef<any>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  if (!director) return null;

  // Retrieve portfolio data from the centralized source based on director ID
  const content = filmmakersContent.find(f => f.id === director.id);
  const projects = content ? content.portfolio : [];

  // Determine active project based on prop or local selection
  const activeProject = activeProjectId 
    ? projects.find(p => p.id === activeProjectId) || null 
    : null;

  const setActiveProject = (project: ProjectData | null) => {
    if (onSelectProject) {
      onSelectProject(project ? project.id : null);
    }
  };

  // Load YouTube Player API if it's not already loaded
  useEffect(() => {
    if (!(window as any).YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);
    }
  }, []);

  // Reset video ended state when project change occurs
  useEffect(() => {
    setVideoEnded(false);
  }, [activeProjectId, director.id]);

  // Append enablejsapi=1 to support YouTube message passing
  const getEmbedUrlWithJsApi = (url: string) => {
    if (!url) return '';
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}enablejsapi=1`;
    }
    return url;
  };

  // Replay handler
  const handleReplay = () => {
    setVideoEnded(false);
    if (playerRef.current && typeof playerRef.current.playVideo === 'function') {
      try {
        playerRef.current.seekTo(0);
        playerRef.current.playVideo();
      } catch (err) {
        setResetKey(prev => prev + 1);
      }
    } else if (videoRef.current) {
      try {
        videoRef.current.currentTime = 0;
        videoRef.current.play();
      } catch (err) {
        setResetKey(prev => prev + 1);
      }
    } else {
      setResetKey(prev => prev + 1);
    }
  };

  // Volver handler
  const handleVolver = () => {
    setVideoEnded(false);
    if (projects.length === 1) {
      // For JJUnicorn single-video inline player: reset video state
      setResetKey(prev => prev + 1);
    } else {
      // For multi-video layout, Volver closes the active modal
      setActiveProject(null);
    }
  };

  // Go to directors handler
  const handleGoToDirectors = () => {
    setVideoEnded(false);
    if (onBack) {
      onBack();
    }
  };

  // Callback ref for YouTube IFrames
  const iframeRefCallback = useCallback((iframe: HTMLIFrameElement | null) => {
    if (!iframe) {
      playerRef.current = null;
      return;
    }

    const checkYT = () => {
      const globalYT = (window as any).YT;
      if (globalYT && globalYT.Player) {
        try {
          playerRef.current = new globalYT.Player(iframe, {
            events: {
              onStateChange: (event: any) => {
                // event.data === 0 represents YT.PlayerState.ENDED
                if (event.data === 0) {
                  setVideoEnded(true);
                }
              }
            }
          });
        } catch (err) {
          console.error('[YouTube API Error] Failed to bind player:', err);
        }
      } else {
        setTimeout(checkYT, 200);
      }
    };

    checkYT();
  }, []);

  // Callback ref for Native Video Elements
  const videoRefCallback = useCallback((videoElement: HTMLVideoElement | null) => {
    videoRef.current = videoElement;
    if (videoElement) {
      const handleNativeEnded = () => setVideoEnded(true);
      videoElement.addEventListener('ended', handleNativeEnded);
      return () => {
        videoElement.removeEventListener('ended', handleNativeEnded);
      };
    }
  }, []);

  // Multi-language button translations
  const langKey = (language || 'ES').toUpperCase();
  const buttonTranslations: Record<string, { replay: string; back: string; directors: string }> = {
    EN: {
      replay: 'Watch Again',
      back: 'Back',
      directors: 'Go to Filmmakers'
    },
    ES: {
      replay: 'Ver nuevamente',
      back: 'Volver',
      directors: 'Ir a Directores'
    },
    PT: {
      replay: 'Assistir novamente',
      back: 'Voltar',
      directors: 'Ir para Diretores'
    },
    FR: {
      replay: 'Regarder à nouveau',
      back: 'Retour',
      directors: 'Aller aux Réalisateurs'
    },
    DE: {
      replay: 'Nochmals abspielen',
      back: 'Zurück',
      directors: 'Zu den Filmemachern'
    }
  };

  const t = buttonTranslations[langKey] || buttonTranslations['ES'];

  // End Screen Render Helper
  const renderEndScreen = () => {
    return (
      <div className="absolute inset-0 bg-black/95 z-50 flex flex-col items-center justify-center p-4 text-center animate-fade-in pointer-events-auto">
        <div className="max-w-md mx-auto flex flex-col items-center">
          {/* Irreverentia Emblem */}
          <img 
            src="https://www.irreverentia.eu/logo-green.png" 
            alt="IRREVERENTIA" 
            className="w-48 sm:w-64 h-auto object-contain mb-8 drop-shadow-[0_0_15px_rgba(204,255,0,0.4)]"
          />
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 w-full px-4">
            {/* Watch Again */}
            <button 
              onClick={handleReplay}
              className="w-full sm:w-auto bg-black text-[#ccff00] font-display font-black text-xs uppercase tracking-wider py-3 px-6 border-2 border-[#ccff00] hover:bg-[#ccff00] hover:text-black hover:shadow-[0_0_15px_rgba(204,255,0,0.4)] active:translate-y-0.5 transition-all flex items-center justify-center gap-2 cursor-none"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-4 h-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
              </svg>
              {t.replay}
            </button>
            
            {/* Back (Volver) */}
            <button 
              onClick={handleVolver}
              className="w-full sm:w-auto bg-zinc-900 text-zinc-100 font-display font-black text-xs uppercase tracking-wider py-3 px-6 border-2 border-zinc-700 hover:bg-zinc-100 hover:text-black hover:border-zinc-100 active:translate-y-0.5 transition-all flex items-center justify-center gap-2 cursor-none"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-4 h-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
              </svg>
              {t.back}
            </button>
            
            {/* Go to Filmmakers */}
            <button 
              onClick={handleGoToDirectors}
              className="w-full sm:w-auto bg-black text-[#ccff00] font-display font-black text-xs uppercase tracking-wider py-3 px-6 border-2 border-zinc-700 hover:border-[#ccff00] hover:bg-zinc-950 active:translate-y-0.5 transition-all flex items-center justify-center gap-2 cursor-none"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-4 h-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.109A11.386 11.386 0 0110.089 20M11.612 11.543a3 3 0 11-4.417 4.417 3 3 0 014.417-4.417zm7.44.03a3 3 0 11-4.4 4.4 3 3 0 014.4-4.4zM2.55 17.6c.09-.947.643-1.785 1.507-2.195a11.23 11.23 0 0112.518-2.196c.864.41 1.417 1.248 1.507 2.196" />
              </svg>
              {t.directors}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="pt-24 pb-20 min-h-screen bg-black text-white px-4 md:px-12 relative">
      <div className="max-w-[1400px] mx-auto animate-fade-in">
        
        {/* Navigation / Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-16 border-b border-zinc-800 pb-8">
          <div>
            <button 
              onClick={onBack}
              className="text-[#ccff00] font-mono text-sm mb-4 hover:underline flex items-center gap-2 cursor-none"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
              </svg>
              BACK TO FILMMAKERS
            </button>
            <h1 className="font-display font-black text-5xl md:text-8xl uppercase tracking-tighter text-white leading-none">
              {director.name}
            </h1>
            <p className="font-mono text-[#ccff00] mt-2 tracking-widest uppercase">{director.role}</p>
          </div>
          <div className="mt-6 md:mt-0 max-w-md">
             <p className="font-mono text-zinc-400 text-sm leading-relaxed border-l-2 border-[#ccff00] pl-4">
               {director.desc}
             </p>
          </div>
        </div>

        {/* Portfolio Content */}
        {projects.length === 1 ? (
          <div className="w-full flex flex-col items-center mt-6">
            <div className="w-full max-w-6xl aspect-video bg-zinc-950 border-2 border-zinc-800 shadow-[0_0_50px_rgba(204,255,0,0.08)] relative group overflow-hidden">
              {/* Decorative HUD corners */}
              <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-[#ccff00] z-10 pointer-events-none"></div>
              <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-[#ccff00] z-10 pointer-events-none"></div>
              <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-[#ccff00] z-10 pointer-events-none"></div>
              <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-[#ccff00] z-10 pointer-events-none"></div>
              
              {/* Top Bar HUD Info */}
              <div className="absolute top-3 left-4 right-4 flex justify-between items-center pointer-events-none z-10">
                <span className="font-mono text-[10px] text-[#ccff00] bg-black/60 px-2 py-0.5 border border-[#ccff00]/20 tracking-wider animate-pulse">
                  ARCHIVE_FEED_01 // SECURE_STREAM
                </span>
                <span className="font-mono text-[10px] text-zinc-500 bg-black/60 px-2 py-0.5 border border-zinc-800 tracking-wider">
                  STATUS: {videoEnded ? 'STREAM_COMPLETED' : 'STREAMING_ACTIVE'}
                </span>
              </div>

              {/* Render custom overlay screen if ended */}
              {videoEnded && renderEndScreen()}

              {projects[0].type === 'local' ? (
                <video 
                  key={`${resetKey}-video`}
                  ref={videoRefCallback}
                  src={projects[0].video} 
                  controls 
                  className="w-full h-full object-contain pt-8 pb-3 px-3 md:pt-10 md:pb-4 md:px-4"
                >
                  Your browser does not support the video tag.
                </video>
              ) : (
                <iframe 
                  key={`${resetKey}-iframe`}
                  ref={iframeRefCallback}
                  src={getEmbedUrlWithJsApi(projects[0].video)} 
                  className="w-full h-full pt-8 pb-3 px-3 md:pt-10 md:pb-4 md:px-4" 
                  frameBorder="0" 
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                  allowFullScreen
                  title={projects[0].title}
                ></iframe>
              )}
            </div>

            {/* Video Info Card below player */}
            <div className="w-full max-w-6xl mt-6 flex flex-col sm:flex-row justify-between items-start sm:items-baseline border-b border-zinc-800 pb-4">
              <div>
                <h3 className="font-display font-black text-2xl md:text-4xl text-white uppercase tracking-tight">
                  {projects[0].title}
                </h3>
                <p className="font-mono text-[#ccff00] text-sm mt-1">
                  RELEASE YEAR: {projects[0].year}
                </p>
              </div>
              <div className="mt-3 sm:mt-0 flex gap-4 items-center">
                <span className="font-mono text-zinc-400 text-xs bg-zinc-900 border border-zinc-800 px-3 py-1">
                  DURATION: {projects[0].duration}
                </span>
              </div>
            </div>
          </div>
        ) : (
          /* Portfolio Grid */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.length > 0 ? (
              projects.map((project) => (
                <div 
                  key={project.id} 
                  className="group cursor-pointer" 
                  onClick={() => setActiveProject(project)}
                >
                  {/* Thumbnail Container */}
                  <div className="relative aspect-video bg-zinc-900 border border-zinc-800 overflow-hidden mb-3">
                    
                    {/* Real Image */}
                    <img 
                      src={project.img} 
                      alt={project.title}
                      className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                    />
                    
                    {/* Play Icon Overlay */}
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-16 h-16 rounded-full border border-white/30 bg-black/30 backdrop-blur-sm flex items-center justify-center group-hover:border-[#ccff00] group-hover:bg-[#ccff00] transition-colors">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24" className="w-6 h-6 text-white group-hover:text-black ml-1">
                               <path d="M8 5v14l11-7z" />
                            </svg>
                        </div>
                    </div>
                    
                    {/* Duration Badge */}
                    <div className="absolute top-2 right-2 bg-black/80 px-2 py-1 border border-white/10">
                       <span className="font-mono text-[10px] text-[#ccff00]">{project.duration}</span>
                    </div>
                  </div>

                  {/* Text Info */}
                  <div className="flex justify-between items-baseline border-b border-zinc-800 pb-2 group-hover:border-[#ccff00] transition-colors">
                    <h3 className="font-display font-bold text-xl uppercase tracking-wide group-hover:text-[#ccff00] transition-colors truncate pr-4">
                       {project.title}
                    </h3>
                    <span className="font-mono text-xs text-zinc-500 whitespace-nowrap">{project.year}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-full py-24 text-center border border-dashed border-zinc-800 flex flex-col items-center justify-center opacity-50">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-12 h-12 mb-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
                <p className="font-mono text-zinc-500">NO PUBLIC ARCHIVES FOUND.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* VIDEO MODAL */}
      {activeProject && (
        <div 
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-md flex items-center justify-center p-4 md:p-10" 
          onClick={() => setActiveProject(null)}
        >
           
           {/* Close Button */}
           <button 
             onClick={() => setActiveProject(null)}
             className="absolute top-4 right-4 md:top-8 md:right-8 text-white hover:text-[#ccff00] z-[110] p-2 cursor-none animate-fade-in"
           >
             <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-8 h-8 md:w-10 md:h-10">
               <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
             </svg>
           </button>

           <div 
             className="w-full max-w-6xl max-h-screen flex flex-col relative" 
             onClick={(e) => e.stopPropagation()}
           >
              
              {/* Video Player Box */}
              <div className="relative aspect-video w-full bg-black border border-zinc-800 shadow-[0_0_50px_rgba(204,255,0,0.1)]">
                 {/* Video overlay if ended */}
                 {videoEnded && renderEndScreen()}

                 {activeProject.type === 'local' ? (
                   <video 
                     key={`${resetKey}-modal-video`}
                     ref={videoRefCallback}
                     src={activeProject.video} 
                     controls 
                     autoPlay 
                     className="w-full h-full object-contain"
                   >
                     Your browser does not support the video tag.
                   </video>
                 ) : (
                   <iframe 
                     key={`${resetKey}-modal-iframe`}
                     ref={iframeRefCallback}
                     src={getEmbedUrlWithJsApi(activeProject.video)} 
                     className="w-full h-full" 
                     frameBorder="0" 
                     allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                     allowFullScreen
                     title={activeProject.title}
                   ></iframe>
                 )}
              </div>

              {/* Video Info */}
              <div className="mt-4 flex justify-between items-start">
                 <div>
                    <h2 className="font-display font-bold text-2xl md:text-3xl text-white uppercase tracking-wide">
                      {activeProject.title}
                    </h2>
                    <p className="font-mono text-[#ccff00] text-sm mt-1">
                      {director.name} // {activeProject.year}
                    </p>
                 </div>
                 <div className="hidden md:block">
                    <span className="font-mono text-zinc-500 text-xs border border-zinc-800 px-2 py-1 rounded">
                      DURATION: {activeProject.duration}
                    </span>
                 </div>
              </div>
           </div>
        </div>
      )}

    </div>
  );
};
